import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, MapPin, ShieldCheck, Phone } from "lucide-react";
import { POSITION_NAMES } from "@/lib/constants";

export default function DispatcherProfessionals() {
  const [pros, setPros] = useState([]);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("all");
  const [availOnly, setAvailOnly] = useState(false);

  useEffect(() => {
    api.get("/professionals").then((r) => setPros(r.data));
  }, []);

  const filtered = pros.filter((p) => {
    const matchQ = `${p.first_name} ${p.last_name} ${p.primary_position}`.toLowerCase().includes(q.toLowerCase());
    const matchPos = pos === "all" || p.primary_position === pos;
    const matchAvail = !availOnly || p.available_now;
    return matchQ && matchPos && matchAvail;
  });

  return (
    <div className="space-y-6" data-testid="dispatcher-professionals">
      <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Professional Network</h1>
      <Card>
        <CardContent className="p-4 flex gap-3 flex-wrap">
          <Input placeholder="Search by name or position…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="pros-search" />
          <Select value={pos} onValueChange={setPos}>
            <SelectTrigger className="w-48" data-testid="pros-position-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All positions</SelectItem>
              {POSITION_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            onClick={() => setAvailOnly(!availOnly)}
            data-testid="pros-avail-toggle"
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${availOnly ? "bg-brand-teal text-white border-brand-teal" : "bg-white text-slate-700 border-slate-300"}`}
          >
            Available Now Only
          </button>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <Card key={p.id} className="hover:border-brand-teal transition-colors" data-testid={`pro-card-${p.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-brand-navy text-white flex items-center justify-center font-bold text-lg">
                  {p.first_name[0]}{p.last_name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500">{p.primary_position}</div>
                </div>
                {p.available_now && <span className="status-pill bg-green-100 text-green-700 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Now</span>}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-slate-500">NetStaff</div><div className="font-bold text-brand-navy flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />{p.netstaff_score}</div></div>
                <div><div className="text-slate-500">Rate/hr</div><div className="font-bold text-brand-navy">${p.hourly_rate}</div></div>
                <div><div className="text-slate-500">Exp</div><div className="font-bold text-brand-navy">{p.experience_years} yrs</div></div>
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</span>
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>
              </div>
              {p.specialties?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.specialties.slice(0, 3).map((s) => (
                    <span key={s} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
